import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { clsx } from 'clsx';
import Button from 'components/Button/Button';
import IconButton from 'components/IconButton/IconButton';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import { BUTTON_VARIANT } from 'constants/interfaces';

import Backdrop from './Backdrop';

import type IModal from './interfaces/IModal';

import styles from './scss/Modal.module.scss';

function Modal({
  open,
  className,
  title,
  titleAction,
  description,
  children,
  onClose,
  onReject,
  onConfirm,
  confirmIcon,
  confirmTitle,
  confirmVariant,
  closeTitle,
  isButtonsVisible = true,
  isLoading,
  showInfoIcon,
  closeClassName,
  titleClassName,
  disabledConfirm,
  additionalBtnTitle,
  onActionAdditionalBtn,
  showLoader,
  hintClassName,
  isFullscreen = false,
}: IModal) {
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else if (document.body.style.overflow === 'hidden') {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return open ? createPortal(
    <Backdrop isFullscreen={isFullscreen}>
      <div className={clsx(styles.root, className)}>
        {onClose ? (
          <IconButton
            className={clsx(styles.close, closeClassName)}
            size="small"
            icon={<CloseIcon />}
            onClick={onClose}
          />
        ) : null}
        <div className={clsx(styles.titleWrapper, titleClassName)}>
          {title ? <h2 className={styles.title}>{title}</h2> : null}
          {titleAction || showLoader ? (
            <div className={styles.titleActions}>
              {titleAction}
              {showLoader ? <SmallLoader size={24} /> : null}
            </div>
          ) : null}
        </div>
        {description ? (
          <p className={clsx(styles.description, hintClassName)}>
            {showInfoIcon ? <InfoIcon className={styles.infoIcon} /> : null}
            {description}
          </p>
        ) : null}
        {children}
        {onReject && onConfirm && isButtonsVisible ? (
          <div className={styles.actions}>
            <Button
              title={closeTitle || t('common.close')}
              variant={BUTTON_VARIANT.cancel}
              onClick={onReject}
            />
            {additionalBtnTitle ? (
              <Button
                title={additionalBtnTitle}
                variant={BUTTON_VARIANT.secondary}
                onClick={onActionAdditionalBtn}
              />
            ) : null}
            <Button
              title={confirmTitle || t('common.confirm')}
              startIcon={confirmIcon}
              variant={confirmVariant}
              onClick={onConfirm}
              isLoading={isLoading}
              disabled={disabledConfirm}
            />
          </div>
        ) : isButtonsVisible && (
          <div className={styles.actions}>
            <Button
              title={closeTitle || t('common.close')}
              onClick={onReject}
            />
          </div>
        )}
      </div>
    </Backdrop>,
    document.body,
  ) : null;
}

export default Modal;
